import { useState, useEffect, useRef } from "react";
import BaseChat from "./BaseChat";
import { getDomainCfg, store } from '../../core/state';
import { translate } from "../../core/i18n";
import * as api from "../../features/chatrequests";
import { CHAT_VIEW_NAME, EDITOR_VIEW_NAME } from "../../core/constants";
import { runChatPostEvaluation } from "../../features/chatevaluate";
import { saveUserData } from "../../core/code/saver";
import { timeStamp } from "console";
import { loadProject } from "../../core/project/projectdata";
import { userHasRoleFromList } from "../../features/cloudstorage/user";

interface SupportChatProps {
  hasProjectContext: boolean;
  resetMessages?: boolean;
}

const SupportChat: React.FC<SupportChatProps> = ({ hasProjectContext, resetMessages }) => {
  let startMessages = [
    {
      user: getDomainCfg().chatSettings?.botName,
      color: "green",
      msg: getDomainCfg().chatSettings?.startMessage,      
    }
  ]

  const [chatState] = store.useState(CHAT_VIEW_NAME);
  const [editorState, setEditorState] = store.useState(EDITOR_VIEW_NAME); 
  const [messages, setMessages] = useState(startMessages);

  const [roomMessages, setRoomMessages] = useState([]);
  const [prevLength, setPrevLength] = useState(0);
  const [evaluatingChat, setEvaluatingChat] = useState(false); // Zustand für die Evaluierung des Chats
  //const [roomDetails, setRoomDetails] = useState({}); 
  const [typingDots, setTypingDots] = useState(0);
  const [typingMessage, setTypingMessage] = useState(translate("chat-server-responding"));
  const [serverIsResponding, setServerIsResponding] = useState(false);
  const [workflowSessionId, setWorkflowSessionId] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  const [wfStatus, setWfStatus] = useState({ status: "idle", message: null, stepState: null, currentStep: null});
  const [userQuota, setUserQuota] = useState(null);
  const [retries, setRetries] = useState(0); // Anzahl der Wiederholungsversuche bei API-Fehlern
  const typingTimer = useRef(null);
  const llmSwitchAvailable = chatState?.cfg?.llmSwitchAvailable === true;
  const modelOptions: { id: string; name: string; roles?: string[] }[] = Array.isArray(chatState?.cfg?.switchModels) ? chatState.cfg.switchModels : [];
  
  // Hilfsfunktion: Prüft ob ein Modell für den aktuellen Benutzer verfügbar ist
  const isModelAvailable = (model: { id: string; name: string; roles?: string[] }): boolean => {
    if (!model.roles || !Array.isArray(model.roles) || model.roles.length === 0) {
      return true; // Modell ohne Rollen-Einschränkung ist verfügbar
    }
    return userHasRoleFromList(model.roles);
  };
  
  // Finde das erste verfügbare Modell
  const getFirstAvailableModel = (): string | undefined => {
    const availableModel = modelOptions.find(opt => isModelAvailable(opt));
    return availableModel?.id;
  };
  
  // Initialisiere mit geladenem Modell oder erstem verfügbaren Modell
  const savedModelId = editorState.data?.lastUsedModelId;
  const savedModel = savedModelId ? modelOptions.find(opt => opt.id === savedModelId) : null;
  const initialModelId = (savedModel && isModelAvailable(savedModel)) ? savedModelId : getFirstAvailableModel();
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(initialModelId);

  useEffect(() => {
    if (editorState.data?.chatMessages) {
      console.log("set initial messages", editorState.data?.chatMessages)
      setRoomMessages(editorState.data?.chatMessages);
    }
    // Lade gespeichertes Modell beim Initialisieren, wenn verfügbar
    if (savedModelId && savedModel && isModelAvailable(savedModel)) {
      setSelectedModelId(savedModelId);
    } else if (!selectedModelId) {
      const availableModelId = getFirstAvailableModel();
      if (availableModelId) {
        setSelectedModelId(availableModelId);
      }
    }
  }, [])

  useEffect(() => {
    const asyncResetMessages = async () => {
      if (resetMessages) {
        console.log("Resetting messages due to resetMessages prop");
        setMessages([]); // Setzen Sie hier Ihre initiale Nachrichtenlogik ein, falls benötigt

        setWorkflowSessionId(null);
        setCurrentRoomId(null);
        setRoomMessages([]);
        setHasSentFirstMessage(false);

        await saveMessagesState([]);
        // Update editorState with empty chat messages
        //await updateMessages();
        
      }
    };
    asyncResetMessages();
  }, [resetMessages]);

  const triggerPostEvaluation = async (messages) => {
    //console.log("triggerPostEvaluation", roomMessages.length, prevLength, roomMessages);    
    if (/*roomMessages.length > prevLength &&*/ !evaluatingChat) {
      if ((editorState.project?.subTasks || editorState.project?.eval) && messages.length > 0) {
        console.log("triggered PostEvaluation", messages);
        setEvaluatingChat(true);
        await runChatPostEvaluation(messages);      
        setEvaluatingChat(false);
      }
    }
  }

  const updateMessages = async () => {
    console.log("updateMessages", roomMessages, editorState, messages);
    const serverMessages = roomMessages.filter(msg => msg.sender !== "System").map(msg => ({
      user: msg.sender,
      color: msg.sender === getDomainCfg().chatSettings.botName ? "green" : "blue", // Farbe anpassen, falls erforderlich
      messageType: msg.role,
      msg: msg.message,
      timestamp: msg.timestamp
    }));

    /*if (prevLength !== serverMessages.length) {
      console.log("triggerPostEvaluation", serverMessages.length, prevLength, serverMessages);    
      triggerPostEvaluation(serverMessages);
      setPrevLength(serverMessages.length);
    }*/

    let  startMessage = { 
      user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
      color: "green",
      messageType: "systemprompt",
      msg: editorState.project?.startMessage || getDomainCfg().chatSettings?.startMessage,
      timestamp: new Date()
    }
    startMessages = [startMessage]

    const quotaMessage = {
      user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
      color: "red", // Quota-Nachricht in Rot anzeigen
      messageType: "warning",
      msg: getDomainCfg().chatSettings?.quotaMessage,
      timestamp: new Date()
    }

    const newMessages = [      
      ...serverMessages
    ];

    //console.log("userQuota", userQuota);
    if (userQuota && userQuota.limit > 0 && userQuota.requests >= userQuota.limit) {      
      // Füge die Quota-Nachricht nur einmal hinzu
      if (!newMessages.some(message => message.msg === getDomainCfg().chatSettings.quotaMessage)) {
        if (serverMessages.length > 0) {
          newMessages.push(quotaMessage);
        } else {
          startMessage = quotaMessage;
        }
      }
    }

    newMessages.unshift(startMessage);
    

    if (serverIsResponding) {
      newMessages.push({
        user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
        color: "green",
        messageType: "typing",
        msg: typingMessage + ".".repeat(typingDots),
        timestamp: new Date()
      });
    }

    console.log("Updating messages:", newMessages); 
    setMessages(newMessages);    
    //setTimeout(() => { triggerPostEvaluation(messages) }, 500); // Rufe die Evaluierungsfunktion auf
    
  };

  const saveMessagesState = async (messagesToSave) => {
    console.log("Saving user data", messagesToSave, roomMessages);
    const updatedEditorState = {
      ...editorState,
      data: {
        ...editorState.data,
        chatMessages: messagesToSave !== undefined ? messagesToSave : roomMessages ,
        rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
      },
    };
    setEditorState(updatedEditorState);

    // Save user data after updating messages
    console.log("Saving user data", editorState);
    await saveUserData();
  }

  const handleModelChange = async (modelId: string) => {
    // Finde das ausgewählte Modell in der Konfiguration
    const selectedModel = modelOptions.find(opt => opt.id === modelId);
    
    // Setze das Modell immer (auch wenn nicht verfügbar)
    setSelectedModelId(modelId);
    
    // Prüfe, ob das Modell für den Benutzer verfügbar ist
    if (!selectedModel || !isModelAvailable(selectedModel)) {
      // Öffne Upgrade-Seite in neuem Tab
      const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
      window.open(upgradeUrl, '_blank');
      // Speichere das Modell trotzdem (für UI-Zustand)
      const updatedEditorState = {
        ...editorState,
        data: {
          ...editorState.data,
          lastUsedModelId: modelId,
          rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
        },
      };
      setEditorState(updatedEditorState);
      // Speichere die Daten
      await saveUserData();
      return;
    }
    
    // Speichere das Modell im editorState
    const updatedEditorState = {
      ...editorState,
      data: {
        ...editorState.data,
        lastUsedModelId: modelId,
        rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
      },
    };
    setEditorState(updatedEditorState);
    // Speichere die Daten
    await saveUserData();
  }

  const addMessage = (who, color, message, messageType?) => {
    setMessages(prev => [...prev, { user: who, color, msg: message, messageType, timestamp : new Date() }]);
  }

  useEffect(() => {
    const authenticate = async () => {
      try {
        const session = await api.authenticateUser();
        console.log("auth: user-session", session); 
        
        await api.setNickname(getDomainCfg().chatSettings?.userName); // TODO user username for logged in user
        setUserQuota(session.quota);
      } catch (error) {
        console.log('Authentifizierungsfehler:', error);
        addMessage(translate("backend-error"), "red", translate("chat-bot-conn-error"), "error");
        setServerIsResponding(false);
      }
    };

    authenticate();
  }, []);

  // Workflow-Status abrufen
  const fetchWorkflowStatus = async () => {
    try {
      if (retries >= 10) {
        addMessage(translate("backend-error"), "red", translate("chat-bot-error"), "error");
        setServerIsResponding(false);
        return;
      }
      if (!workflowSessionId || !currentRoomId) return;
      const statusResponse = await api.fetchWorkflowStatus(workflowSessionId);
      const status = statusResponse.response;
      const oldStatus = { ...wfStatus };
      setWfStatus(status);

      setPrevLength(roomMessages.length);
      const messagesResult = await api.fetchRoomMessages(currentRoomId);
      setRoomMessages(messagesResult.messages);

      //const details = await api.fetchRoomDetails(currentRoomId);
      //setRoomDetails(details);
  
      console.log("status", status);
      updateTypingMessage(status);
      setUserQuota(status.quota);
      // Überprüfen Sie, ob sich der Status geändert hat
      if (oldStatus.status !== status.status || oldStatus.stepState !== status.stepState) {
        
        // Logik zur Aktualisierung der Benutzeroberfläche basierend auf dem Workflow-Status
        switch (status.stepState) {
          case "PROCESSING":
            // Zeigen Sie eine "Eintipp"-Nachricht an
            setServerIsResponding(true);
            break;
          case "WAITING":
          case "FINISHED":
          case "STOPPED":
            // Entfernen Sie die "Eintipp"-Nachricht
            setServerIsResponding(false);
            setMessages(prevMessages => prevMessages.slice(0, -1));
            triggerPostEvaluation(messagesResult.messages);
            await saveMessagesState(messagesResult.messages);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('Fehler beim Abrufen des Workflow-Status:', error);      
      setRetries(retries + 1);
    }
  };
  
  useEffect(() => {
    console.log("roomMessages changed", roomMessages, userQuota);
    updateMessages();
  }, [roomMessages, userQuota]);

  useEffect(() => {    
    if (serverIsResponding) {      
      fetchWorkflowStatus();
      typingTimer.current = setTimeout(() => {
        setTypingDots((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setTypingDots(0);
    }

    return () => {
      clearTimeout(typingTimer.current);
    };
  }, [typingDots, serverIsResponding]);

  const updateTypingMessage = (workflowStatus) => {
    const statusMessages = chatState.cfg.statusMessages;
    const currentStep = workflowStatus.currentStep;

    const matchingMessage = statusMessages.find((message) => message.step === currentStep);

    if (matchingMessage) {
      setTypingMessage(matchingMessage.message);
    }
  };

  const sendMessage = async (msg) => {
    console.log("sendMessage called with:", msg); 
    const userMessage = {
      user: getDomainCfg().chatSettings.userName,
      color: "blue",
      messageType: "user",
      msg: msg,
      timestamp: new Date()
    };

    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, userMessage];
      console.log("Messages after user message added:", updatedMessages); 
      //triggerPostEvaluation(updatedMessages);  // Aufruf nach Hinzufügen der Nachricht
      return updatedMessages;
    });
  
    // Starten Sie den "Eintipp"-Effekt
    setServerIsResponding(true);
  
    try {
      // Initialisierung des Workflows 'cokibot' beim Senden der ersten Nachricht
      let workflowData;
      if (!hasSentFirstMessage) {
        const userContext = editorState.data?.projectContent?.contextContent; 
        const wfDefinition = {
          ...editorState.project?.definition,
          context: { userContext }
        }
        console.log("initializeWorkflow", wfDefinition, userContext);
        workflowData = await api.initializeWorkflow(
          editorState.project?.workflow || getDomainCfg().chatSettings.workflow,
          wfDefinition,
          editorState.data?.chatMessages,
          llmSwitchAvailable ? selectedModelId : undefined
        );
        setWorkflowSessionId(workflowData.sessionId);
        setCurrentRoomId(workflowData.roomId);
        setHasSentFirstMessage(true);
        // Hier könnten Sie weitere Schritte nach der Initialisierung durchführen
      }
  
      // Verwenden der neuen API-Funktion zum Senden einer Nachricht
      const roomId = currentRoomId || workflowData.roomId; 
      const result = await api.sendMessage(roomId, msg);
      console.log("result after sending message", result);
  
      if (result.error) {
        // Fehlerbehandlung
        addMessage(translate("backend-error"), "red", translate("chat-bot-error"), "error");
        setServerIsResponding(false);
      } 
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      addMessage(translate("backend-error"), "red", translate("chat-bot-error"), "error");
      setServerIsResponding(false);
    }
  };
  
  console.log("render Chat", editorState, messages);
  return <BaseChat
    messages={messages}
    sendMessage={sendMessage}
    serverIsResponding={serverIsResponding}
    hasProjectContext={hasProjectContext}
    resetChat={resetMessages}
    llmSwitchAvailable={llmSwitchAvailable}
    modelOptions={modelOptions}
    selectedModelId={selectedModelId}
    onChangeModel={handleModelChange}
    isModelAvailable={isModelAvailable}
  />;
};

export default SupportChat;